/**
 * ═══════════════════════════════════════════════════════════════
 * Hasımoğlu Çiftliği – Ana Sayfa / Tanıtım (Landing Page)
 * ───────────────────────────────────────────────────────────────
 * Premium tasarımlı, animasyonlu, full-scroll tanıtım sayfası.
 * Bölümler: Hero, Hakkımızda, Hizmetler, Galeri, SSS, İletişim
 * 
 * Özellikler:
 *   - Parallax scroll efektleri
 *   - Floating partiküller
 *   - 3D kart hover animasyonları
 *   - Sayı sayma animasyonu
 *   - Staggered reveal animasyonları
 *   - Geometrik arka plan desenleri
 * ═══════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LogIn, ShoppingCart, ChevronDown, Shield, Truck, Eye, Phone, 
  MapPin, Clock, Star, CheckCircle2, ArrowRight, Menu, X,
  Beef, Scale, Heart, Users, Award, MessageCircle, ChevronRight
} from 'lucide-react';

// ── Animated Counter Hook ──
function useCountUp(end, duration = 2000, startCounting = false) {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  
  useEffect(() => {
    if (!startCounting) return;
    let startTime = null;
    const numEnd = parseInt(end);
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // easeOutExpo
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(eased * numEnd));
      if (progress < 1) {
        countRef.current = requestAnimationFrame(animate);
      }
    };
    
    countRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(countRef.current);
  }, [end, duration, startCounting]);
  
  return count;
}

// ── Floating Particles Component ──
function FloatingParticles() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
      {Array.from({ length: 20 }).map((_, i) => {
        const size = 2 + Math.random() * 4;
        const left = Math.random() * 100;
        const delay = Math.random() * 15;
        const duration = 15 + Math.random() * 20;
        const opacity = 0.1 + Math.random() * 0.3;
        
        return (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: size + 'px',
              height: size + 'px',
              left: left + '%',
              bottom: '-5%',
              background: `radial-gradient(circle, rgba(234,179,8,${opacity}) 0%, transparent 70%)`,
              animation: `floatUp ${duration}s ${delay}s linear infinite`,
            }}
          />
        );
      })}
    </div>
  );
}

// ── Geometric Background Pattern ──
function GeometricBg({ scrollY }) {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.03] z-0">
      {/* Rotating large geometric shapes */}
      <div 
        className="absolute -top-32 -right-32 w-96 h-96 border border-gold-500 rounded-full"
        style={{ transform: `rotate(${scrollY * 0.02}deg) scale(${1 + scrollY * 0.0002})` }}
      />
      <div 
        className="absolute top-1/3 -left-20 w-72 h-72 border border-gold-500"
        style={{ transform: `rotate(${45 + scrollY * 0.03}deg)` }}
      />
      <div 
        className="absolute bottom-1/4 right-1/4 w-48 h-48 border border-gold-500 rounded-full"
        style={{ transform: `rotate(${-scrollY * 0.015}deg)` }}
      />
      {/* Diamond shape */}
      <div 
        className="absolute top-2/3 left-1/3 w-36 h-36 border border-gold-400"
        style={{ transform: `rotate(${45 + scrollY * 0.025}deg) translateY(${Math.sin(scrollY * 0.005) * 20}px)` }}
      />
      {/* Small decorative circles */}
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="absolute w-3 h-3 rounded-full bg-gold-500/20"
          style={{
            top: `${15 + i * 12}%`,
            left: `${10 + (i % 3) * 35}%`,
            transform: `translateY(${Math.sin(scrollY * 0.003 + i) * 30}px)`,
          }}
        />
      ))}
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [activeFaq, setActiveFaq] = useState(null);
  const [isVisible, setIsVisible] = useState({});
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mouse tracking for 3D tilt
  const handleMouseMove = useCallback((e) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
            if (entry.target.id === 'stats-row') {
              setStatsVisible(true);
            }
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );
    
    document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  // 3D Tilt calculation for cards
  const getTiltStyle = (cardRef) => {
    if (!cardRef?.current) return {};
    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const rotateX = ((mousePos.y - centerY) / rect.height) * -8;
    const rotateY = ((mousePos.x - centerX) / rect.width) * 8;
    return {
      transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`,
      transition: 'transform 0.1s ease-out',
    };
  };

  const features = [
    { icon: <Shield className="w-7 h-7" />, title: 'Güvenilir & Şeffaf', desc: 'Tüm kurbanlarımız veteriner kontrolünden geçirilir. Küpe numarası ve canlı ağırlık bilgisi ile şeffaf satış.', color: 'from-emerald-500 to-emerald-700', glow: 'group-hover:shadow-emerald-500/20' },
    { icon: <Eye className="w-7 h-7" />, title: 'Canlı Takip Sistemi', desc: 'Dijital platformumuzda hisse durumunuzu, ödeme geçmişinizi ve kurbanınızı anlık olarak takip edebilirsiniz.', color: 'from-blue-500 to-blue-700', glow: 'group-hover:shadow-blue-500/20' },
    { icon: <Scale className="w-7 h-7" />, title: 'Hisseli Kurban Kolaylığı', desc: 'Büyükbaş kurbanlarımızda 1-7 arası hisse alabilir, ödemenizi taksitlendirebilirsiniz.', color: 'from-gold-500 to-amber-600', glow: 'group-hover:shadow-gold-500/20' },
    { icon: <Truck className="w-7 h-7" />, title: 'Profesyonel Kesim & Teslimat', desc: 'Dini vecibelere uygun, hijyenik koşullarda profesyonel ekibimiz tarafından kesim hizmeti.', color: 'from-purple-500 to-purple-700', glow: 'group-hover:shadow-purple-500/20' },
    { icon: <Users className="w-7 h-7" />, title: 'Vekâlet Hizmeti', desc: 'Şehir dışında veya yurt dışında olsanız bile vekâlet ile kurbanınızı kestirebilirsiniz.', color: 'from-rose-500 to-rose-700', glow: 'group-hover:shadow-rose-500/20' },
    { icon: <Heart className="w-7 h-7" />, title: 'Aile Geleneği', desc: 'Yıllardır ailelerden ailelere aktarılan güven ile 30+ yıllık hayvancılık tecrübesi.', color: 'from-teal-500 to-teal-700', glow: 'group-hover:shadow-teal-500/20' },
  ];

  const stats = [
    { value: '30', label: 'Yıllık Tecrübe', suffix: '+' },
    { value: '5000', label: 'Mutlu Müşteri', suffix: '+' },
    { value: '3', label: 'Hizmet Şubesi', suffix: '' },
    { value: '100', label: 'Memnuniyet Oranı', suffix: '%' },
  ];

  const stat0 = useCountUp(stats[0].value, 2000, statsVisible);
  const stat1 = useCountUp(stats[1].value, 2500, statsVisible);
  const stat2 = useCountUp(stats[2].value, 1500, statsVisible);
  const stat3 = useCountUp(stats[3].value, 2000, statsVisible);
  const statValues = [stat0, stat1, stat2, stat3];

  const faqs = [
    { q: 'Hisseli kurban nasıl çalışır?', a: 'Büyükbaş kurbanlıklarımız 7 hisseye bölünür. 1 ile 7 arası dilediğiniz kadar hisse satın alabilirsiniz. Her hisse sahibi ayrı niyet edebilir. Kesim sonrası etler eşit şekilde paylaştırılır.' },
    { q: 'Ödeme nasıl yapılır?', a: 'Nakit, kredi kartı veya havale/EFT ile ödeme kabul ediyoruz. Peşin veya taksitli ödeme seçenekleri mevcuttur. Her ödemede dijital makbuz düzenlenir.' },
    { q: 'Kurbanımı takip edebilir miyim?', a: 'Evet! Dijital platformumuz üzerinden kurbanınızın küpe numarası, kilosu, hisse durumu, ödeme geçmişi ve kesim sırasını 7/24 anlık olarak takip edebilirsiniz.' },
    { q: 'Kesim ne zaman ve nerede yapılır?', a: 'Kesim, Kurban Bayramı\'nın 1. günü başlamak üzere şubelerimizde gerçekleştirilir. Veteriner gözetiminde, hijyenik ve İslami usullere uygun şekilde profesyonel kasaplarımız tarafından yapılır.' },
    { q: 'Vekâlet ile kurban kestirmek istiyorum, ne yapmalıyım?', a: 'Platformumuzda hisse satın alırken "Vekâlet" seçeneğini işaretleyebilirsiniz. Niyet duaları ekibimiz tarafından okunarak kesim gerçekleştirilir ve size WhatsApp üzerinden bildirim yapılır.' },
  ];

  return (
    <div className="min-h-screen bg-navy-950 text-white overflow-x-hidden">
      
      {/* ── GLOBAL KEYFRAMES ── */}
      <style>{`
        @keyframes floatUp {
          0% { transform: translateY(0) translateX(0) scale(1); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 0.5; }
          100% { transform: translateY(-110vh) translateX(${Math.random() > 0.5 ? '' : '-'}40px) scale(0.3); opacity: 0; }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes morphBlob {
          0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          25% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
          50% { border-radius: 50% 60% 30% 60% / 30% 50% 70% 50%; }
          75% { border-radius: 40% 60% 50% 40% / 60% 40% 60% 30%; }
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(234,179,8,0.15); }
          50% { box-shadow: 0 0 40px rgba(234,179,8,0.3); }
        }
        @keyframes slideInLeft {
          from { transform: translateX(-60px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideInRight {
          from { transform: translateX(60px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes fadeInScale {
          from { transform: scale(0.85); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-slide-left { animation: slideInLeft 0.8s ease-out forwards; }
        .animate-slide-right { animation: slideInRight 0.8s ease-out forwards; }
        .animate-fade-scale { animation: fadeInScale 0.6s ease-out forwards; }
        .shimmer-text {
          background: linear-gradient(90deg, #ffffff 0%, #eab308 25%, #ffffff 50%, #eab308 75%, #ffffff 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 4s linear infinite;
        }
        .gradient-border {
          position: relative;
        }
        .gradient-border::before {
          content: '';
          position: absolute;
          inset: -1px;
          border-radius: inherit;
          background: linear-gradient(135deg, rgba(234,179,8,0.3), transparent, rgba(16,185,129,0.3));
          z-index: -1;
          background-size: 200% 200%;
          animation: gradientShift 6s ease infinite;
        }
        .card-3d {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          transform-style: preserve-3d;
        }
        .card-3d:hover {
          transform: perspective(1000px) rotateX(-2deg) rotateY(3deg) translateZ(10px) scale(1.03);
        }
      `}</style>

      {/* ── Floating Particles ── */}
      <FloatingParticles />
      
      {/* ── Geometric Background ── */}
      <GeometricBg scrollY={scrollY} />

      {/* ═══════════════════════════════════════════ */}
      {/* ── NAVBAR ──────────────────────────────── */}
      {/* ═══════════════════════════════════════════ */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrollY > 80 
          ? 'bg-navy-950/95 backdrop-blur-xl shadow-2xl shadow-black/20 border-b border-navy-800/50' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <div className="flex items-center gap-3 cursor-pointer group" onClick={() => scrollToSection('hero')}>
              <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl overflow-hidden shadow-lg border border-white/10 bg-navy-950/50 group-hover:border-gold-500/50 transition-all group-hover:shadow-gold-500/20">
                <img src="/images/angry_bull_logo.png" alt="Logo" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-sm lg:text-base font-extrabold text-white tracking-wide leading-tight">HAŞİMOĞLU</h1>
                <p className="text-[9px] lg:text-[10px] text-gold-400 font-bold uppercase tracking-[0.2em]">Çiftliği</p>
              </div>
            </div>

            <div className="hidden lg:flex items-center gap-1">
              {[
                { label: 'Ana Sayfa', id: 'hero' },
                { label: 'Hakkımızda', id: 'about' },
                { label: 'Hizmetler', id: 'features' },
                { label: 'Galeri', id: 'gallery' },
                { label: 'SSS', id: 'faq' },
                { label: 'İletişim', id: 'contact' },
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="px-4 py-2 text-sm font-medium text-navy-300 hover:text-white transition-all rounded-lg hover:bg-white/5 relative group"
                >
                  {item.label}
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gold-500 group-hover:w-2/3 transition-all duration-300 rounded-full" />
                </button>
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              <button
                onClick={() => navigate('/katalog')}
                className="px-4 py-2.5 text-sm font-bold text-navy-300 hover:text-white transition-all border border-navy-700 hover:border-gold-500/50 rounded-xl hover:shadow-lg hover:shadow-gold-500/5"
              >
                <span className="flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Katalog</span>
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-5 py-2.5 text-sm font-bold text-navy-950 bg-gold-500 hover:bg-gold-400 transition-all rounded-xl shadow-lg shadow-gold-500/20 flex items-center gap-2 hover:scale-105 active:scale-95"
                style={{ animation: 'pulseGlow 3s ease-in-out infinite' }}
              >
                <LogIn className="w-4 h-4" /> Giriş Yap
              </button>
            </div>

            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-xl text-white hover:bg-navy-800 transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-navy-950/98 backdrop-blur-xl border-b border-navy-800 shadow-2xl">
            <div className="px-4 py-3 space-y-1">
              {['Ana Sayfa:hero', 'Hakkımızda:about', 'Hizmetler:features', 'Galeri:gallery', 'SSS:faq', 'İletişim:contact'].map(item => {
                const [label, id] = item.split(':');
                return (
                  <button key={id} onClick={() => scrollToSection(id)} className="w-full text-left px-4 py-3 text-sm font-medium text-navy-300 hover:text-white hover:bg-navy-800/50 rounded-lg transition-colors">
                    {label}
                  </button>
                );
              })}
              <div className="pt-3 border-t border-navy-800 flex gap-2">
                <button onClick={() => navigate('/katalog')} className="flex-1 py-3 text-sm font-bold text-navy-300 border border-navy-700 rounded-xl text-center">Katalog</button>
                <button onClick={() => navigate('/login')} className="flex-1 py-3 text-sm font-bold text-navy-950 bg-gold-500 rounded-xl text-center">Giriş Yap</button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* ═══════════════════════════════════════════ */}
      {/* ── HERO SECTION ────────────────────────── */}
      {/* ═══════════════════════════════════════════ */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Parallax */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/hero_farm_banner.png" 
            alt="Hasımoğlu Çiftliği" 
            className="w-full h-full object-cover will-change-transform"
            style={{ transform: `translateY(${scrollY * 0.35}px) scale(${1 + scrollY * 0.0003})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-950/60 via-navy-950/30 to-navy-950" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-950/80 via-transparent to-navy-950/40" />
        </div>

        {/* Animated Gradient Orbs */}
        <div className="absolute inset-0 z-5 pointer-events-none overflow-hidden">
          <div 
            className="absolute w-[600px] h-[600px] rounded-full bg-gold-500/5 blur-[120px]"
            style={{ 
              top: '10%', left: '60%',
              animation: 'morphBlob 20s ease-in-out infinite',
              transform: `translate(${Math.sin(scrollY * 0.002) * 50}px, ${Math.cos(scrollY * 0.002) * 30}px)`,
            }}
          />
          <div 
            className="absolute w-[400px] h-[400px] rounded-full bg-forest-500/5 blur-[100px]"
            style={{ 
              top: '40%', left: '10%',
              animation: 'morphBlob 15s ease-in-out infinite reverse',
              transform: `translate(${Math.cos(scrollY * 0.003) * 40}px, ${Math.sin(scrollY * 0.003) * 20}px)`,
            }}
          />
        </div>

        {/* Ottoman Motif Overlay with scroll fade */}
        <div 
          className="absolute top-0 left-0 right-0 h-40 z-10 pointer-events-none" 
          style={{ 
            backgroundImage: 'url(/images/ottoman_trust_bg.png)', 
            backgroundSize: 'cover', 
            backgroundPosition: 'top center',
            opacity: Math.max(0.15 - scrollY * 0.0005, 0),
          }} 
        />

        {/* Hero Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20"
             style={{ transform: `translateY(${scrollY * -0.15}px)`, opacity: Math.max(1 - scrollY * 0.002, 0) }}>
          <div className="max-w-4xl mx-auto">
            {/* Badge */}
            <div 
              className="inline-flex items-center gap-2 bg-gold-500/10 border border-gold-500/30 rounded-full px-5 py-2 mb-8 backdrop-blur-sm"
              style={{ animation: 'fadeInScale 0.8s ease-out 0.2s both' }}
            >
              <Star className="w-4 h-4 text-gold-400 animate-spin" style={{ animationDuration: '8s' }} />
              <span className="text-sm font-bold text-gold-400 tracking-wide">2026 Kurban Sezonu Hisseleri Açıldı</span>
            </div>

            <h1 
              className="text-4xl sm:text-5xl lg:text-7xl font-black text-white leading-[1.1] mb-6 tracking-tight"
              style={{ animation: 'fadeInScale 0.8s ease-out 0.4s both' }}
            >
              Güvenin Adresi,
              <br />
              <span className="shimmer-text">
                Hasımoğlu Çiftliği
              </span>
            </h1>

            <p 
              className="text-lg sm:text-xl text-navy-200 max-w-2xl mx-auto mb-10 leading-relaxed font-medium"
              style={{ animation: 'fadeInScale 0.8s ease-out 0.6s both' }}
            >
              30 yılı aşan tecrübemizle, İslami usullere uygun, şeffaf ve güvenilir kurbanlık hisse satışı. 
              Dijital platformumuzda kurbanınızı anlık takip edin.
            </p>

            {/* CTA Buttons */}
            <div 
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
              style={{ animation: 'fadeInScale 0.8s ease-out 0.8s both' }}
            >
              <button
                onClick={() => navigate('/katalog')}
                className="group w-full sm:w-auto px-8 py-4 bg-gold-500 hover:bg-gold-400 text-navy-950 rounded-2xl font-extrabold text-base transition-all shadow-2xl shadow-gold-500/30 hover:shadow-gold-500/50 hover:scale-[1.05] active:scale-[0.96] flex items-center justify-center gap-3 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <ShoppingCart className="w-5 h-5 relative z-10" />
                <span className="relative z-10">Kurbanlıkları İncele</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform relative z-10" />
              </button>
              <button
                onClick={() => navigate('/login')}
                className="w-full sm:w-auto px-8 py-4 bg-white/5 hover:bg-white/10 text-white border border-white/20 hover:border-white/40 rounded-2xl font-bold text-base transition-all backdrop-blur-sm flex items-center justify-center gap-3 hover:scale-[1.03]"
              >
                <LogIn className="w-5 h-5" />
                Müşteri Girişi
              </button>
            </div>

            {/* Animated Stats */}
            <div 
              id="stats-row" 
              data-animate
              className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto"
              style={{ animation: 'fadeInScale 0.8s ease-out 1s both' }}
            >
              {stats.map((stat, i) => (
                <div 
                  key={i} 
                  className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 hover:bg-white/10 transition-all duration-300 hover:border-gold-500/30 hover:shadow-lg hover:shadow-gold-500/5 card-3d cursor-default"
                >
                  <p className="text-2xl sm:text-3xl font-black text-gold-400 tabular-nums">
                    {statValues[i]}{stat.suffix}
                  </p>
                  <p className="text-xs sm:text-sm text-navy-300 font-medium mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
            <span className="text-[10px] text-navy-400 font-medium uppercase tracking-widest">Keşfet</span>
            <div className="w-6 h-10 rounded-full border-2 border-navy-600 flex items-start justify-center p-1">
              <div className="w-1.5 h-3 bg-gold-400 rounded-full animate-bounce" />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* ── HAKKIMIZDA SECTION ──────────────────── */}
      {/* ═══════════════════════════════════════════ */}
      <section id="about" className="relative py-24 lg:py-32 overflow-hidden">
        {/* Section background orbs */}
        <div className="absolute inset-0 pointer-events-none">
          <div 
            className="absolute w-[500px] h-[500px] rounded-full bg-gold-500/3 blur-[120px] -right-48 top-0"
            style={{ transform: `translateY(${(scrollY - 800) * 0.1}px)` }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image with parallax */}
            <div 
              id="about-image" 
              data-animate
              className={`relative group transition-all duration-1000 ${isVisible['about-image'] ? 'animate-slide-left' : 'opacity-0 -translate-x-16'}`}
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-gold-500/20 to-forest-500/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-navy-700/50 card-3d">
                <img 
                  src="/images/premium_daytime_farm_background.png" 
                  alt="Çiftliğimiz" 
                  className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-navy-900/80 backdrop-blur-sm rounded-xl p-4 border border-navy-700/50 gradient-border transform group-hover:translate-y-0 translate-y-2 transition-transform duration-500">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gold-500/10 flex items-center justify-center">
                        <Award className="w-7 h-7 text-gold-400" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white">30+ Yıllık Güven</p>
                        <p className="text-xs text-navy-300">Ailelerden ailelere aktarılan kalite</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Text */}
            <div 
              id="about-text" 
              data-animate
              className={`transition-all duration-1000 delay-200 ${isVisible['about-text'] ? 'animate-slide-right' : 'opacity-0 translate-x-16'}`}
            >
              <div className="inline-flex items-center gap-2 bg-forest-500/10 border border-forest-500/30 rounded-full px-4 py-1.5 mb-6">
                <Beef className="w-4 h-4 text-forest-400" />
                <span className="text-xs font-bold text-forest-400 uppercase tracking-wider">Bizi Tanıyın</span>
              </div>
              
              <h2 className="text-3xl lg:text-4xl font-black text-white mb-6 tracking-tight leading-tight">
                Geleneksel Değerler,
                <br />
                <span className="text-gold-400">Modern Hizmet</span>
              </h2>

              <div className="space-y-4 text-navy-200 leading-relaxed">
                <p>
                  <strong className="text-white">Hasımoğlu Çiftliği</strong> olarak, ailemizden ailenize 30 yılı aşkın süredir güvenilir hayvancılık hizmeti sunuyoruz. Kurban bayramını bir ibadet bilinci ile hazırlıyor, hayvanlarımızı titizlikle yetiştirip, siz değerli müşterilerimize ulaştırıyoruz.
                </p>
                <p>
                  Geniş meralarda, doğal yemlerle beslenen kurbanlıklarımız veteriner hekim kontrolünde, sağlık belgeli olarak hizmetinize sunulmaktadır. Dijital platformumuz sayesinde kurban alım sürecinizi tamamen online olarak yönetebilirsiniz.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-8">
                {[
                  { icon: <CheckCircle2 className="w-5 h-5 text-forest-400" />, text: 'Veteriner Kontrollü' },
                  { icon: <CheckCircle2 className="w-5 h-5 text-forest-400" />, text: 'Sağlık Belgeli' },
                  { icon: <CheckCircle2 className="w-5 h-5 text-forest-400" />, text: 'Doğal Besleme' },
                  { icon: <CheckCircle2 className="w-5 h-5 text-forest-400" />, text: 'Dijital Takip' },
                ].map((item, i) => (
                  <div 
                    key={i} 
                    className="flex items-center gap-2.5 group/item hover:translate-x-1 transition-transform duration-300"
                    style={{ animationDelay: `${0.8 + i * 0.15}s` }}
                  >
                    <div className="group-hover/item:scale-110 transition-transform">{item.icon}</div>
                    <span className="text-sm font-semibold text-white">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* ── HİZMETLER SECTION ───────────────────── */}
      {/* ═══════════════════════════════════════════ */}
      <section id="features" className="relative py-24 lg:py-32 bg-navy-900/30 overflow-hidden">
        {/* Moving gradient orb */}
        <div className="absolute inset-0 pointer-events-none">
          <div 
            className="absolute w-[600px] h-[600px] rounded-full bg-purple-500/3 blur-[150px]"
            style={{ 
              left: '50%', top: '50%',
              transform: `translate(-50%, -50%) translate(${Math.sin(scrollY * 0.001) * 100}px, ${Math.cos(scrollY * 0.001) * 60}px)`,
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div 
            id="features-header" 
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ${isVisible['features-header'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <div className="inline-flex items-center gap-2 bg-gold-500/10 border border-gold-500/30 rounded-full px-4 py-1.5 mb-6">
              <Star className="w-4 h-4 text-gold-400" />
              <span className="text-xs font-bold text-gold-400 uppercase tracking-wider">Neden Biz?</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-black text-white tracking-tight">
              Farkımız, <span className="text-gold-400">Güvenimiz</span>
            </h2>
            <p className="text-navy-300 mt-4 max-w-2xl mx-auto text-base">
              Kurban ibadeti hassas bir konu. Biz bunu ciddiyetle yapıyoruz.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div 
                key={i}
                id={`feature-${i}`}
                data-animate
                className={`group relative bg-navy-800/30 hover:bg-navy-800/60 border border-navy-700/50 hover:border-navy-600 rounded-2xl p-6 transition-all duration-500 hover:shadow-2xl ${feature.glow} card-3d cursor-default overflow-hidden ${
                  isVisible[`feature-${i}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                {/* Hover glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
                
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-5 text-white shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-gold-400 transition-colors">{feature.title}</h3>
                <p className="text-sm text-navy-300 leading-relaxed relative z-10">{feature.desc}</p>
                
                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold-500/50 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* ── GALERİ SECTION ──────────────────────── */}
      {/* ═══════════════════════════════════════════ */}
      <section id="gallery" className="relative py-24 lg:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            id="gallery-header" 
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ${isVisible['gallery-header'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <h2 className="text-3xl lg:text-4xl font-black text-white tracking-tight">
              Çiftliğimizden <span className="text-gold-400">Kareler</span>
            </h2>
            <p className="text-navy-300 mt-4 max-w-xl mx-auto">
              Geniş meralarımız, bakımlı tesislerimiz ve sağlıklı hayvanlarımız
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { src: '/images/gallery_4.webp', alt: 'Çiftlik Girişi', caption: 'Çiftliğimizin ana giriş yolu' },
              { src: '/images/gallery_2.webp', alt: 'Aile Ortamı', caption: 'Sıcak bir aile ortamında kurban geleneği' },
              { src: '/images/gallery_3.webp', alt: 'Bayram Kalabalığı', caption: 'Kurban bayramı hazırlıkları' },
              { src: '/images/gallery_1.webp', alt: 'Tesis Alanı', caption: 'Geniş tesis alanımız ve araç parkı' },
            ].map((img, i) => (
              <div 
                key={i} 
                id={`gallery-${i}`}
                data-animate
                className={`group relative rounded-2xl overflow-hidden border border-navy-700/50 shadow-lg hover:shadow-2xl hover:shadow-gold-500/10 transition-all duration-500 hover:border-gold-500/30 card-3d ${
                  isVisible[`gallery-${i}`] ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
                }`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <img src={img.src} alt={img.alt} className="w-full aspect-[4/3] object-cover group-hover:scale-110 transition-transform duration-[1200ms] ease-out" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 via-navy-950/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                  <p className="text-sm font-bold text-white drop-shadow-lg">{img.alt}</p>
                  <p className="text-xs text-navy-200 mt-0.5">{img.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* ── SSS SECTION ────────────────────────── */}
      {/* ═══════════════════════════════════════════ */}
      <section id="faq" className="relative py-24 lg:py-32 bg-navy-900/30 overflow-hidden">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            id="faq-header" 
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ${isVisible['faq-header'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <h2 className="text-3xl lg:text-4xl font-black text-white tracking-tight">
              Sıkça Sorulan <span className="text-gold-400">Sorular</span>
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div 
                key={i}
                id={`faq-${i}`}
                data-animate
                className={`border rounded-2xl transition-all duration-500 overflow-hidden ${
                  activeFaq === i ? 'border-gold-500/50 bg-navy-800/40 shadow-lg shadow-gold-500/5' : 'border-navy-700/50 bg-navy-800/20 hover:bg-navy-800/30 hover:border-navy-600'
                } ${isVisible[`faq-${i}`] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left group"
                >
                  <span className="font-bold text-white text-sm sm:text-base pr-4 group-hover:text-gold-400 transition-colors">{faq.q}</span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 shrink-0 ${
                    activeFaq === i ? 'bg-gold-500 text-navy-950 rotate-180' : 'bg-navy-700 text-navy-300 group-hover:bg-navy-600'
                  }`}>
                    <ChevronDown className="w-4 h-4" />
                  </div>
                </button>
                <div className={`transition-all duration-500 ease-in-out ${activeFaq === i ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="px-5 pb-5 text-sm text-navy-300 leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* ── CTA BANNER ─────────────────────────── */}
      {/* ═══════════════════════════════════════════ */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/ottoman_trust_bg.png" alt="" className="w-full h-full object-cover opacity-10" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/90 to-navy-950" />
        </div>
        
        {/* Animated gradient line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/50 to-transparent" style={{ backgroundSize: '200% 100%', animation: 'gradientShift 4s ease infinite' }} />

        <div 
          id="cta-section" 
          data-animate
          className={`relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10 transition-all duration-1000 ${isVisible['cta-section'] ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
        >
          <h2 className="text-3xl lg:text-5xl font-black text-white mb-6 tracking-tight">
            Kurban İbadetiniz <span className="text-gold-400">Güvenli Ellerde</span>
          </h2>
          <p className="text-navy-200 text-lg max-w-2xl mx-auto mb-10">
            Hisselerimiz hızla dolmaktadır. Yerinizi ayırın, gerisini bize bırakın.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/katalog')}
              className="group w-full sm:w-auto px-10 py-4 bg-gold-500 hover:bg-gold-400 text-navy-950 rounded-2xl font-extrabold text-lg transition-all shadow-2xl shadow-gold-500/25 hover:scale-[1.05] flex items-center justify-center gap-3 relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <span className="relative z-10">Hissemi Seç</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform relative z-10" />
            </button>
            <a
              href="https://wa.me/905373202533"
              target="_blank"
              rel="noreferrer"
              className="w-full sm:w-auto px-10 py-4 bg-forest-600 hover:bg-forest-500 text-white rounded-2xl font-bold text-lg transition-all flex items-center justify-center gap-3 hover:scale-[1.03] hover:shadow-lg hover:shadow-forest-500/20"
            >
              <MessageCircle className="w-5 h-5" /> WhatsApp İletişim
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* ── İLETİŞİM & FOOTER ──────────────────── */}
      {/* ═══════════════════════════════════════════ */}
      <section id="contact" className="relative py-20 bg-navy-900/50 border-t border-navy-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            id="footer-content" 
            data-animate
            className={`grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12 transition-all duration-1000 ${isVisible['footer-content'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {/* Brand */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img src="/images/angry_bull_logo.png" alt="Logo" className="w-10 h-10 rounded-xl border border-white/10" />
                <div>
                  <h3 className="font-extrabold text-white">HAŞİMOĞLU</h3>
                  <p className="text-[10px] text-gold-400 font-bold tracking-widest">ÇİFTLİĞİ</p>
                </div>
              </div>
              <p className="text-sm text-navy-400 leading-relaxed">
                Güvenin adresi, aileden aileye aktarılan 30 yıllık kurban geleneği.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Hızlı Bağlantılar</h4>
              <div className="space-y-2.5">
                {['Kurbanlık Kataloğu', 'Müşteri Girişi', 'Hakkımızda', 'SSS'].map((link, i) => (
                  <button key={i} onClick={() => {
                    if (link === 'Kurbanlık Kataloğu') navigate('/katalog');
                    else if (link === 'Müşteri Girişi') navigate('/login');
                    else if (link === 'Hakkımızda') scrollToSection('about');
                    else scrollToSection('faq');
                  }} className="block text-sm text-navy-400 hover:text-gold-400 transition-colors font-medium group">
                    <span className="flex items-center gap-1.5"><ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" /> {link}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">İletişim</h4>
              <div className="space-y-3">
                <a href="tel:+905373202533" className="flex items-center gap-3 text-sm text-navy-400 hover:text-white transition-colors group">
                  <Phone className="w-4 h-4 text-gold-400 group-hover:scale-110 transition-transform" /> 0537 320 25 33
                </a>
                <div className="flex items-start gap-3 text-sm text-navy-400">
                  <MapPin className="w-4 h-4 text-gold-400 mt-0.5 shrink-0" /> 
                  <span>Ovacık, Yayladere Cad. No:87, 41420 Gebze / Kocaeli</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-navy-400">
                  <Clock className="w-4 h-4 text-gold-400" /> 08:00 – 20:00 (Her Gün)
                </div>
              </div>
            </div>

            {/* Map */}
            <div>
              <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Konumumuz</h4>
              <div className="rounded-xl overflow-hidden border border-navy-700/50 shadow-lg h-36 hover:border-gold-500/30 transition-colors">
                <iframe
                  title="Hasımoğlu Çiftliği Konum"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3021.5!2d29.43!3d40.80!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cadb9!2sOvac%C4%B1k%2C+Gebze!5e0!3m2!1str!2str!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'grayscale(1) invert(1) opacity(0.7)' }}
                  allowFullScreen=""
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="pt-8 border-t border-navy-800/50 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-navy-500">
              © 2026 Hasımoğlu Çiftliği. Tüm hakları saklıdır.
            </p>
            <p className="text-xs text-navy-600">
              Kurban Yönetim Platformu v2.0
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
